import React from 'react'
import { useSelector } from 'react-redux'
import type { GridProps } from '@mui/material'
import { type Nil, TextWithTooltip } from '@pbt/pbt-ui-components'

import PatientInfoLabel from '~/components/common/labels/PatientInfoLabel'
import Link from '~/components/common/link/Link'
import PatientWarningChip from '~/components/common/PatientWarningChip'
import { getPatient } from '~/store/reducers/patients'
import type { TimetableEvent } from '~/types'

import { PatientPreferencesChip } from '../dashboard/clients/patient/PatientPreferencesChip'
import LinkedChewyAccountContainer, {
  KyriosAccountType,
} from '../dashboard/link-chewy-account/LinkedChewyAccountContainer'
import WellnessPlanMemberButton from '../dashboard/wellness-plans/WellnessPlanMemberButton'
import EntityDisplay from './EntityDisplay'

interface PatientDisplayProps extends GridProps {
  appointment?: TimetableEvent
  clientId: string | Nil
  isLink?: boolean
  patientId: string | Nil
}

/**
 * Displays patient information in a consistent layout:
 * name > chips > info
 */
const PatientDisplay = ({
  appointment,
  clientId,
  isLink = false,
  patientId,
  ...props
}: PatientDisplayProps) => {
  const patient = useSelector(getPatient(patientId))

  if (!patient) {
    return null
  }

  const nameContent = (
    <TextWithTooltip noIcon strong underline={isLink}>
      {patient.name}
    </TextWithTooltip>
  )

  const name = isLink ? (
    <Link
      id="patient-display-link"
      target="_blank"
      to={`/patient/${patientId}`}
    >
      {nameContent}
    </Link>
  ) : (
    nameContent
  )

  const chips = (
    <>
      <PatientWarningChip patientId={patientId} />
      <PatientPreferencesChip appointment={appointment} patientId={patientId} />
      <LinkedChewyAccountContainer
        accountType={KyriosAccountType.PATIENT}
        clientId={clientId}
        patientId={patientId}
      />
      <WellnessPlanMemberButton
        chip
        readonly
        clientId={clientId}
        patientId={patientId}
      />
    </>
  )

  const info = <PatientInfoLabel patientId={patientId} />

  return <EntityDisplay chips={chips} info={info} name={name} {...props} />
}

export default PatientDisplay
